import { useCallback } from "react";

import { notify } from "../../../components/Notification";
import {
  useUpdateUserDetailsMutation,
  useUpdateUserPasswordMutation,
} from "../../../services";
import { PasswordFormData, UserDetails, UserPreferences } from "../types";
import { UseSettingsActionsResult } from "./types";

/**
 * The Settings write surface. Both profile and preference saves hit
 * `PATCH /auth/details`, which invalidates the `User` tag — so the page's own
 * read refreshes without a hand-wired refetch.
 */
export const useSettingsActions = (): UseSettingsActionsResult => {
  const [updateUserDetails] = useUpdateUserDetailsMutation();
  const [updateUserPassword] = useUpdateUserPasswordMutation();

  const handleUpdateProfile = useCallback(
    async (changedData: Partial<UserDetails>) => {
      try {
        await updateUserDetails(changedData).unwrap();
        notify.success("Profile updated successfully!");
        return true;
      } catch {
        return false;
      }
    },
    [updateUserDetails],
  );

  const handleUpdatePassword = useCallback(
    async (passwordObject: PasswordFormData) => {
      try {
        await updateUserPassword(passwordObject).unwrap();
        notify.success("Password updated successfully!");
        return true;
      } catch {
        return false;
      }
    },
    [updateUserPassword],
  );

  const handleUpdatePreferences = useCallback(
    async (changedData: Partial<UserPreferences>) => {
      try {
        await updateUserDetails(changedData).unwrap();
        notify.success("Preferences updated successfully!");
      } catch {
        // errorToastMiddleware raises the toast; keep the form as-is.
      }
    },
    [updateUserDetails],
  );

  return { handleUpdateProfile, handleUpdatePassword, handleUpdatePreferences };
};
